/**
 * WHICH SENTENCE THE READER GETS WHEN A FEED FAILS. Extracted from `api.ts::getJson` so it can be
 * ARMED — the repo's own extract-the-decision law, applied to the line that broke.
 *
 * ⛔⛔ WHY THIS FILE EXISTS AT ALL, AND IT IS NOT TIDINESS.
 * `api.ts` carries a TS parameter property, so **Node's strip-only test runner cannot import it**
 * (api.ts says so itself, at its own imports). The message-selection therefore lived where no arm
 * could reach it — and on 2026-08-22 08:0xZ it was WRONG on the owner's deployed page for every
 * route that answers 200-with-`ok:false`:
 * ```
 *   §GEX-2 rendered:  "0DTE ladder unavailable — read-API returned HTTP 200"
 * ```
 * ⇒ ***THE UNARMABLE LINE IS THE ONE THAT WAS WRONG. That is not a coincidence — it is selection:
 * a decision nothing can test is a decision nothing did test.***
 *
 * ⛔ THE DEFECT ITSELF: the worker has TWO error conventions and the client knew ONE.
 * ```
 *   index.ts:268  fail()  -> { ok:false, error, detail }    the NON-200 path
 *   index.ts:1777 ladder  -> { ok:false, root, reason }     200-with-ok:false
 *   index.ts:1875 levels  -> { ok:false, root, reason }     (also :714 :866 :1017)
 * ```
 * Reading `error` alone dropped the route's honest sentence and fell through to a status fallback.
 * ⚠ AND THE FALLBACK IS WHAT HID IT: a default that always produces something plausible turns a
 * dropped field into a cosmetic oddity instead of a fault. **A default that can never look wrong is
 * a default that can never be caught.**
 */

/** The not-ok wire shapes this fleet actually serves. Both are live; neither is deprecated. */
export interface FeedErrorBody {
  ok?: boolean;
  /** `fail()`'s field — the non-200 path. Keyed contract, keeps precedence. */
  error?: string;
  /** The 200-with-`ok:false` field, served by the dealer routes. */
  reason?: string;
  detail?: string | null;
}

/**
 * Pick the message a panel will show.
 *
 * ⚠ ORDER IS DELIBERATE AND LOAD-BEARING: `error` FIRST, so every route that already worked is
 * untouched and this is strictly additive. `reason` second. The status fallback LAST, and only when
 * the body genuinely said nothing — which is now the only case where a reader sees a bare HTTP code.
 */
export function feedErrorMessage(body: FeedErrorBody | null | undefined, status: number): string {
  const b = body ?? {};
  if (typeof b.error === "string" && b.error.length > 0) return b.error;
  if (typeof b.reason === "string" && b.reason.length > 0) return b.reason;
  return `read-API returned HTTP ${status}`;
}

/**
 * Should this response be treated as a failure at all?
 *
 * ⛔ TWO INDEPENDENT CHECKS, and the second is the one that matters here: **a 200 carrying
 * `ok:false` is still a failure.** Trusting the status alone is how a fail-soft response walks in
 * wearing a success code — this fleet's own `A 200 FROM THE WRONG ORIGIN` law, one layer down.
 */
export function isFeedFailure(httpOk: boolean, body: FeedErrorBody | null | undefined): boolean {
  return !httpOk || body?.ok === false;
}

/** R4: preserve the wire value; validation belongs to readRefusedRows. */
export function feedRefusedRows(body: unknown): unknown {
  return body && typeof body === "object" && Object.hasOwn(body, "refused_rows")
    ? (body as { refused_rows: unknown }).refused_rows
    : null;
}
